import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import Badge from "../../components/ui/Badge";
import { Code, Key, Webhook, Book } from "lucide-react";
import styled from "styled-components";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 64px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const APIGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const APICard = styled.div`
  padding: 32px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const APIIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: white;
`;

const APITitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const APIDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const apiSections = [
  { icon: Key, title: "Authentication", description: "Learn how to authenticate your API requests" },
  { icon: Code, title: "Endpoints", description: "Explore all available API endpoints and methods" },
  { icon: Webhook, title: "Webhooks", description: "Set up webhooks to receive real-time updates" },
  { icon: Book, title: "SDKs", description: "Use our SDKs for popular programming languages" },
];

export default function APIReference() {
  return (
    <Layout>
      <SEO
        title="API Reference"
        description="Complete API documentation for integrating Kliq CRM into your applications."
        keywords="API, documentation, integration, webhooks, SDK"
        hreflang
      />
      <PageTransition>
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">API Reference</Badge>
              </BadgeWrapper>
              <Title>
                Build with our <GradientText>API</GradientText>
              </Title>
              <Description>
                Complete API documentation for integrating Kliq CRM into your applications. RESTful endpoints, webhooks, and SDKs.
              </Description>
            </HeroContent>

            <APIGrid>
              {apiSections.map((section) => (
                <APICard key={section.title}>
                  <APIIcon>
                    <section.icon size={24} />
                  </APIIcon>
                  <APITitle>{section.title}</APITitle>
                  <APIDescription>{section.description}</APIDescription>
                </APICard>
              ))}
            </APIGrid>
          </Container>
        </Section>
      </PageTransition>
    </Layout>
  );
}
